// src/services/gameService.js
// ─────────────────────────────────────────────────────────────────
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "./firebase";

const gamesRef = collection(db, "games");

/**
 * Get all active games (for store page).
 */
export async function getGames(genre = null) {
  let q = query(gamesRef, where("active", "==", true), orderBy("createdAt", "desc"));

  if (genre) {
    q = query(gamesRef, where("active", "==", true), where("genre", "array-contains", genre));
  }

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get all games including inactive (admin use).
 */
export async function getAllGames() {
  const snap = await getDocs(query(gamesRef, orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get a single game by ID.
 */
export async function getGame(gameId) {
  const snap = await getDoc(doc(db, "games", gameId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Upload a game cover image to Firebase Storage.
 * Returns the download URL.
 */
export async function uploadGameImage(file, gameId) {
  const imageRef = ref(storage, `games/${gameId}_${Date.now()}`);
  await uploadBytes(imageRef, file);
  return await getDownloadURL(imageRef);
}

/**
 * Add a new game (admin only).
 */
export async function addGame(gameData, imageFile) {
  // Create game document first to get ID
  const docRef = await addDoc(gamesRef, {
    ...gameData,
    stock: 0,
    active: true,
    coverImage: "",
    createdAt: serverTimestamp(),
  });

  // Upload image if provided
  if (imageFile) {
    const imageUrl = await uploadGameImage(imageFile, docRef.id);
    await updateDoc(docRef, { coverImage: imageUrl });
  }

  return docRef.id;
}

/**
 * Update a game (admin only).
 */
export async function updateGame(gameId, updates, imageFile = null) {
  const gameRef = doc(db, "games", gameId);

  if (imageFile) {
    const imageUrl = await uploadGameImage(imageFile, gameId);
    updates.coverImage = imageUrl;
  }

  await updateDoc(gameRef, { ...updates, updatedAt: serverTimestamp() });
}

/**
 * Soft-delete a game (set active = false).
 */
export async function deactivateGame(gameId) {
  await updateDoc(doc(db, "games", gameId), {
    active: false,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Hard-delete a game (admin only, use with caution).
 */
export async function deleteGame(gameId) {
  await deleteDoc(doc(db, "games", gameId));
}
